import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X } from "lucide-react";

/**
 * Cross-platform PWA install banner.
 *   • Android/Desktop Chrome: uses beforeinstallprompt event
 *   • iOS Safari: shows manual instructions (no beforeinstallprompt on iOS)
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    if (localStorage.getItem("pwa-install-dismissed") === "1") return;

    const ua = navigator.userAgent || "";
    const iOS = /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
    if (iOS) {
      setIsIOS(true);
      setTimeout(() => setVisible(true), 4000);
      return;
    }
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem("pwa-install-dismissed", "1");
  };
  const install = async () => {
    if (isIOS) { setShowIOSHint(true); return; }
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (err) { console.warn("install userChoice error:", err); }
    setDeferred(null);
    setVisible(false);
    localStorage.setItem("pwa-install-dismissed", "1");
  };

  if (!visible) return null;
  return (
    <div className="fixed bottom-3 inset-x-3 sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm z-50 border border-[#D4AF37]/40 bg-[#181A1F]/95 backdrop-blur-md p-4 shadow-2xl rounded-sm"
         data-testid="pwa-install-banner">
      <button onClick={dismiss} data-testid="pwa-install-dismiss"
              className="absolute top-2 right-2 text-[#6E6B64] hover:text-[#E6E1D6]"><X size={14} /></button>
      <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">Install App</div>
      <div className="mt-1 text-sm text-[#E6E1D6] font-light">
        {isIOS
          ? "Open in Safari and tap the Share icon → Add to Home Screen."
          : "Add Jyotish + Homeopathy to your device for one-tap access."}
      </div>
      {showIOSHint && (
        <div className="mt-2 text-[11px] text-[#A8896C] font-mono leading-relaxed">
          Safari → <b>Share</b> (□↑) → <b>Add to Home Screen</b> → <b>Add</b>
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <Button data-testid="pwa-install-btn" onClick={install} size="sm"
                className="rounded-sm bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158]">
          <Download size={14} className="mr-2" /> {isIOS ? "How to install" : "Install"}
        </Button>
        <Button onClick={dismiss} size="sm" variant="ghost"
                className="text-[#A19E95] hover:text-[#D4AF37] hover:bg-transparent">
          Not now
        </Button>
      </div>
    </div>
  );
}
